<template>
  <div v-loading.fullscreen="loading">
    <div class="header">
      <div class="header-actions">
        <el-button @click="$router.back()">
          ← Орқага
        </el-button>
        <div class="header-actions-right">
          <el-button
            type="success"
            :disabled="!selectedBoardId || !measuredAt"
            :loading="excelDownloading"
            @click="downloadMeasuredExcel"
          >
            <el-icon style="margin-right:4px"><Download /></el-icon>
            Jadval sifatida yuklab olish
          </el-button>
          <el-button type="success" :disabled="!selectedBoardId" @click="openTop100Modal">
            ТОР-100 МФЙ киритиш
          </el-button>
          <el-button
            type="primary"
            :disabled="!selectedBoardId"
            :loading="recalculating"
            @click="recalculate"
          >
            Қайта хисоблаш
          </el-button>
          <el-button
            type="warning"
            :disabled="!selectedBoardId || !measuredAt"
            @click="confirmPublish"
          >
            Publish
          </el-button>
        </div>
      </div>

      <div class="header-filters">
        <el-select
          v-model="selectedBoardId"
          placeholder="Доскани tanlang"
          @change="onBoardChange"
          :disabled="recalculating"
          style="min-width: 160px"
        >
          <el-option v-for="b in boards" :key="b.id" :value="b.id" :label="boardLabel(b)" />
        </el-select>

        <el-select
          v-model="selectedRegionId"
          placeholder="Viloyatni tanlang"
          @change="onRegionChange"
          :loading="regionsLoading"
          :disabled="recalculating"
          clearable
        >
          <el-option v-for="r in regions" :key="r.id" :value="r.id" :label="r.title" />
        </el-select>

        <el-select
          v-model="selectedDistrictId"
          placeholder="Tumanni tanlang"
          @change="loadMeasured"
          :loading="districtsLoading"
          :disabled="!selectedRegionId || recalculating"
          clearable
        >
          <el-option v-for="d in districts" :key="d.id" :value="d.id" :label="d.title" />
        </el-select>

        <el-select
          v-model="selectedMeasuredRecessed"
          placeholder="Ҳолати"
          @change="onMeasuredRecessedChange"
          :disabled="recalculating"
          style="min-width: 200px"
        >
          <el-option value="all" label="Барчаси" />
          <el-option :value="true" label="Танаффусдаги маҳаллалар" />
          <el-option :value="false" label="Иштирокчи маҳаллалар" />
        </el-select>
      </div>
    </div>

    <el-table
      :data="quarters"
      border
      height="calc(100vh - 280px)"
      v-loading="tableLoading"
      :row-class-name="rowClassName"
    >
      <el-table-column type="index" width="50" :index="indexMethod" />

      <el-table-column label="Маҳалла номи" min-width="200">
        <template #default="{ row }">
          <div>{{ row.quarter_title }}</div>
          <div style="font-size:11px;color:#909399">ID: {{ row.quarter_id }}</div>
        </template>
      </el-table-column>

      <el-table-column label="Ҳудуд / Туман" min-width="180">
        <template #default="{ row }">
          <div>{{ row.region_title }}</div>
          <div style="font-size:11px;color:#909399">{{ row.district_title }}</div>
        </template>
      </el-table-column>

      <el-table-column label="Маҳалла типи" min-width="110" align="center">
        <template #default="{ row }">
          <span :class="row.categorized_status === 'special' ? 'type-special' : ''">
            {{ row.categorized_status === 'special' ? 'махсус' : 'Нормал' }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="Ғолиб лойиҳалар сони" align="center">
        <el-table-column
          v-for="col in allBoardColumns"
          :key="`col_${col.board_id}`"
          :label="col.label"
          min-width="150"
          align="center"
        >
          <template #default="{ row }">
            <span>{{ getWinnerCount(row, col.board_id) }}</span>
            <el-tag
              v-if="isTopForBoard(row, col.board_id)"
              type="success"
              size="small"
              style="margin-left:4px"
            >Top-100</el-tag>
          </template>
        </el-table-column>
      </el-table-column>

      <el-table-column label="Жами ғолиблар" min-width="100" align="center">
        <template #default="{ row }">{{ row.winner_count }}</template>
      </el-table-column>

      <el-table-column label="Танаффусга" min-width="120" align="center">
        <template #default="{ row }">
          <el-tag v-if="row.measured_recessed" type="success">✓ Ҳа</el-tag>
          <el-tooltip
            v-else-if="row.cannot_recess_reasons && row.cannot_recess_reasons.length"
            :content="cannotRecessText(row.cannot_recess_reasons)"
            placement="top"
          >
            <span class="text-muted">Йўқ</span>
          </el-tooltip>
          <span v-else class="text-muted">Йўқ</span>
        </template>
      </el-table-column>

      <el-table-column label="Базада" min-width="90" align="center">
        <template #default="{ row }">
          <el-tag v-if="row.is_recessed" type="info" size="small">Ҳа</el-tag>
          <span v-else class="text-muted">—</span>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination">
      <el-pagination
        v-model:current-page="page"
        v-model:page-size="pageSize"
        :page-sizes="[20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next"
        @current-change="loadMeasured"
        @size-change="onSizeChange"
      />
    </div>

    <!-- TOP-100 Upload Modal -->
    <el-dialog v-model="showTop100Modal" :show-close="false" width="460px" title="ТОР-100 МФЙ киритиш">
      <el-select
        v-model="top100BoardId"
        placeholder="Doska tanlang"
        style="width: 100%"
      >
        <el-option v-for="b in boards" :key="b.id" :value="b.id" :label="boardLabel(b)" />
      </el-select>
      <template #footer>
        <el-upload
          v-if="top100BoardId"
          :action="`${baseApiUrl}api/v1/top-quarters/upload-excel`"
          :headers="uploadHeaders"
          :data="{ board_id: top100BoardId }"
          :multiple="false"
          accept=".xlsx,.xls"
          :limit="1"
          :show-file-list="false"
          :on-success="onTop100Success"
          :on-error="onTop100Error"
          :on-exceed="() => {}"
          style="display: inline-block; margin-right: 12px"
        >
          <template #trigger>
            <el-button type="primary">Excel faylni tanlang va yuklang</el-button>
          </template>
        </el-upload>
        <el-button @click="showTop100Modal = false">Yopish</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from 'element-plus';
import { Download } from '@element-plus/icons-vue';

export default {
  name: 'RecessMeasured',
  components: { Download },
  data() {
    return {
      loading: false,
      tableLoading: false,
      recalculating: false,
      excelDownloading: false,
      regionsLoading: false,
      districtsLoading: false,

      boards: [],
      regions: [],
      districts: [],
      referenceBoards: [],
      quarters: [],
      measuredAt: null,

      selectedBoardId: null,
      selectedRegionId: null,
      selectedDistrictId: null,
      selectedMeasuredRecessed: true,

      total: 0,
      page: 1,
      pageSize: 20,

      showTop100Modal: false,
      top100BoardId: null,
    };
  },
  computed: {
    baseApiUrl() {
      return process.env.VUE_APP_BASE_API_URL;
    },
    uploadHeaders() {
      return { Authorization: this.$store.state.accessToken };
    },
    selectedBoard() {
      return this.boards.find((b) => b.id === this.selectedBoardId) || null;
    },
    selectedBoardLabel() {
      if (!this.selectedBoard) return 'Мавсум';
      return `${this.selectedBoard.year} йил ${this.selectedBoard.season}-мавсум`;
    },
    publishCount() {
      return this.total;
    },
    allBoardColumns() {
      return [...this.referenceBoards]
        .sort((a, b) => b.board_order - a.board_order)
        .map((ref) => ({
          board_id: ref.board_id,
          label: `${ref.year} йил ${ref.season}-мавсум`,
        }));
    },
  },
  methods: {
    indexMethod(index) {
      return (this.page - 1) * this.pageSize + index + 1;
    },

    boardLabel(board) {
      return `${board.title} ${board.season}-мавсум ${board.year} йил`;
    },

    getWinnerCount(row, boardId) {
      return row.winner_counts_by_board?.find(b => b.board_id === boardId)?.winner_count ?? 0;
    },

    isTopForBoard(row, boardId) {
      return row.top_status_by_board?.find(b => b.board_id === boardId)?.is_top ?? false;
    },

    cannotRecessText(reasons) {
      return reasons
        .map((r) => {
          if (r === 'two_previous_board_pairs_have_no_winners') {
            return "Oldingi mavsumlarda g'olib tashabbuslar mavjud emas";
          }
          return r;
        })
        .join(', ');
    },

    rowClassName({ row }) {
      if (row.measured_recessed) return 'measured-recess-row';
      return '';
    },

    loadBoards() {
      this.loading = true;
      this.$http
        .get('api/v1/boards')
        .then(({ data }) => {
          this.boards = data.boards || [];
          if (this.boards.length) {
            this.selectedBoardId = this.boards[0].id;
            this.loadMeasured().finally(() => { this.loading = false; });
          } else {
            this.loading = false;
          }
        })
        .catch(() => {
          ElNotification.error({ message: 'Doskalar yuklanmadi' });
          this.loading = false;
        });
    },

    loadRegions() {
      this.regionsLoading = true;
      this.$http
        .get('/api/v1/regions')
        .then(({ data }) => { this.regions = data.regions || []; })
        .catch(() => {})
        .finally(() => { this.regionsLoading = false; });
    },

    onRegionChange() {
      this.selectedDistrictId = null;
      this.districts = [];
      this.page = 1;
      if (this.selectedRegionId) {
        this.districtsLoading = true;
        this.$http
          .get('/api/v1/districts', { params: { region_id: this.selectedRegionId } })
          .then(({ data }) => { this.districts = data.districts || []; })
          .catch(() => {})
          .finally(() => { this.districtsLoading = false; });
      }
      this.loadMeasured();
    },

    onBoardChange() {
      this.page = 1;
      this.referenceBoards = [];
      this.measuredAt = null;
      this.selectedMeasuredRecessed = true;
      this.loadMeasured();
    },

    onMeasuredRecessedChange() {
      this.page = 1;
      this.loadMeasured();
    },

    recalculate() {
      if (!this.selectedBoardId) return;
      this.recalculating = true;
      this.tableLoading = true;
      this.$http
        .post('api/v1/state-budget/recess-quarters/measured/recalculate', {
          board_id: this.selectedBoardId,
          lang: 'uz_cyr',
        })
        .then(({ data }) => {
          this.measuredAt = data.measured_at || null;
          this.referenceBoards = data.reference_boards || [];
          this.page = 1;
          this.loadMeasured();
        })
        .catch((err) => {
          ElNotification.error({
            message: err?.response?.data?.message || 'Hisoblashda xatolik',
          });
        })
        .finally(() => { this.recalculating = false; });
    },

    loadMeasured() {
      if (!this.selectedBoardId) return Promise.resolve();
      this.tableLoading = true;
      const params = {
        board_id: this.selectedBoardId,
        region_id: this.selectedRegionId ?? 0,
        district_id: this.selectedDistrictId ?? 0,
        lang: 'uz_cyr',
        limit: this.pageSize,
        offset: (this.page - 1) * this.pageSize,
        measured_recessed: this.selectedMeasuredRecessed,
      };
      return this.$http
        .get('api/v1/state-budget/recess-quarters/measured', { params })
        .then(({ data }) => {
          this.quarters = data.quarters || [];
          this.total = data.count || 0;
          this.referenceBoards = data.reference_boards || [];
          this.measuredAt = data.measured_at || null;
        })
        .catch((err) => {
          ElNotification.error({
            message: err?.response?.data?.message || "Ma'lumot yuklanmadi",
          });
        })
        .finally(() => { this.tableLoading = false; });
    },

    confirmPublish() {
      ElMessageBox.confirm(
        `${this.selectedBoardLabel}: бутун доска учун эски танаффус рўйхати алмаштирилади (жами: ${this.total} та). Филтрлар рўйхатга таъсир қилмайди. Давом этасизми?`,
        'Publish тасдиқлаш',
        {
          confirmButtonText: 'Ҳа, publish',
          cancelButtonText: 'Бекор қилиш',
          type: 'warning',
        }
      )
        .then(() => this.publishMeasured())
        .catch(() => {
          ElMessage.info('Publish бекор қилинди');
        });
    },

    publishMeasured() {
      this.loading = true;
      this.$http
        .post('api/v1/state-budget/recess-quarters/measured/publish', {
          board_id: this.selectedBoardId,
          lang: 'uz_cyr',
        })
        .then(() => {
          ElNotification.success({ message: 'Muvaffaqiyatli publish qilindi!' });
          this.loadMeasured();
        })
        .catch((err) => {
          ElNotification.error({
            message: err?.response?.data?.message || 'Publish qilishda xatolik',
          });
        })
        .finally(() => { this.loading = false; });
    },

    onSizeChange() {
      this.page = 1;
      this.loadMeasured();
    },

    downloadMeasuredExcel() {
      if (!this.selectedBoardId) return;
      this.excelDownloading = true;
      this.$http
        .get('api/v1/state-budget/recess-quarters/measured/excel', {
          params: { board_id: this.selectedBoardId },
          responseType: 'blob',
        })
        .then(({ data, headers }) => {
          const disposition = headers['content-disposition'] || '';
          const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n]+)\1/);
          const filename = match ? match[2] : `measured-recess-${this.selectedBoardId}.xlsx`;
          const url = URL.createObjectURL(new Blob([data]));
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        })
        .catch((err) => {
          ElNotification.error({
            message: err?.response?.data?.message || "Excelni yuklab olishda xatolik",
          });
        })
        .finally(() => { this.excelDownloading = false; });
    },

    openTop100Modal() {
      this.top100BoardId = this.selectedBoardId;
      this.showTop100Modal = true;
    },

    onTop100Success() {
      this.showTop100Modal = false;
      ElNotification.success({ message: "TOP-100 ro'yxati muvaffaqiyatli yuklandi!" });
      this.loadMeasured();
    },

    onTop100Error(error) {
      if (error.status === 401) {
        this.$store.commit('signOut');
        this.$router.push({ name: 'Auth' });
        return;
      }
      ElNotification.error({
        message:
          JSON.parse(error?.message)?.message ||
          "Yuklashda xatolik sodir bo'ldi",
      });
    },
  },

  mounted() {
    this.loadBoards();
    this.loadRegions();
  },
};
</script>

<style scoped>
.header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
}
.header-actions {
  display: flex;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
}
.header-actions-right {
  display: flex;
  gap: 8px;
  align-items: center;
}
.header-filters {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
}
.header .el-button {
  margin-left: 0 !important;
  margin-right: 0 !important;
}
.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}
.type-special {
  color: #f56c6c;
  font-weight: bold;
}
.text-muted {
  color: #c0c4cc;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>

<style>
.measured-recess-row td {
  background-color: rgba(103, 194, 58, 0.12) !important;
}
</style>
